import React, { Component, Fragment } from 'react'
import { Form, Formik } from 'formik'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { H6, Text } from 'app/common/typography'
import { Error, Input } from 'app/common/forms'
import { object, string } from 'yup'
import { CircleChecked, Mail } from 'app/assets/icons'
import { Recaptcha } from 'app/common/recaptcha'

const { red, yellow } = colors

type Props = {
    sendMail: (string, string) => void,
    email: string,
    resetPasswordMailError?: any,
    fromEcommerce?: boolean,
    goBack: () => void
}

type State = {
    showCaptcha: boolean
}

class MailFormComponent extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    render() {
        const { email, sendMail, resetPasswordMailError, goBack } = this.props
        const { showCaptcha } = this.state
        const initialValues: any = {
            email: ''
        }

        const formSubscription = () => {
            const mandatory = 'form.error.field.mandatory'
            return object().shape({
                email: string().required(mandatory)
            })
        }

        return (
            <Fragment>
                {showCaptcha && (
                    <Recaptcha
                        action="WebMailResetPwd"
                        verifyCallback={this.verifyCallback}
                    />
                )}
                {email ? (
                    <Fragment>
                        <FormattedMessage id="reset.password.mail.success.title">
                            {(text: string) => <StyledH6>{text}</StyledH6>}
                        </FormattedMessage>
                        <Paragraph center>
                            <Mail size={50} />
                        </Paragraph>
                        <FormattedMessage id="reset.password.mail.success">
                            {(text: string) => <Text>{text}</Text>}
                        </FormattedMessage>
                        <FormattedMessage id="reset.password.mail.success.resend.link">
                            {(text: string) => (
                                <RetryLink
                                    onClick={async () => {
                                        try {
                                            this.setState({ showCaptcha: true })
                                            const recaptchaToken = await this
                                                .recaptchaPromise
                                            sendMail(email, recaptchaToken)
                                        } finally {
                                            this.setState({
                                                showCaptcha: false
                                            })
                                            this.initRecaptchaPromise()
                                        }
                                    }}>
                                    {text}
                                </RetryLink>
                            )}
                        </FormattedMessage>
                        <Paragraph center>
                            <Text>{email}</Text>
                        </Paragraph>
                        <Paragraph center>
                            <CircleChecked />
                        </Paragraph>
                        <StyledConnect onClick={goBack} type="button">
                            <FormattedMessage id="reset.password.mail.success.button.label" />
                        </StyledConnect>
                    </Fragment>
                ) : (
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        onSubmit={async (values, actions) => {
                            try {
                                this.setState({ showCaptcha: true })
                                const recaptchaToken = await this
                                    .recaptchaPromise
                                await sendMail(
                                    values.email.trim(),
                                    recaptchaToken
                                )
                            } finally {
                                this.setState({ showCaptcha: false })
                                this.initRecaptchaPromise()
                                actions.setSubmitting(false)
                            }
                        }}
                        validationSchema={formSubscription()}>
                        {({ errors, touched, isSubmitting }) => (
                            <Form>
                                <FormattedMessage id="reset.password.mail.form.message">
                                    {(text: string) => <Text>{text}</Text>}
                                </FormattedMessage>
                                <Input
                                    name="email"
                                    type="email"
                                    labelKey="reset.password.form.email.title"
                                    placeholderKey="form.email.placeholder"
                                    className={
                                        errors.email && touched.email
                                            ? 'error'
                                            : ''
                                    }
                                />
                                {errors.email && touched.email && (
                                    <Error errorKey={errors.email} />
                                )}
                                <ButtonContainer>
                                    <StyledButtonMedium
                                        type="submit"
                                        background={yellow}
                                        disabled={isSubmitting}>
                                        <FormattedMessage id="reset.password.mail.form.button.label" />
                                    </StyledButtonMedium>
                                </ButtonContainer>
                                {resetPasswordMailError && (
                                    <Mentions>
                                        <FormattedMessage
                                            id={resetPasswordMailError}>
                                            {(text: string) => (
                                                <Text color={red}>{text}</Text>
                                            )}
                                        </FormattedMessage>
                                    </Mentions>
                                )}
                                <Paragraph center>
                                    <FormattedMessage id="reset.password.mail.return.message.start">
                                        {(text: string) => <Text>{text}</Text>}
                                    </FormattedMessage>
                                    <FormattedMessage id="reset.password.mail.return.message.link">
                                        {(text: string) => (
                                            <ReturnLink
                                                onClick={goBack}
                                                type="button">
                                                <Text>{text}</Text>
                                            </ReturnLink>
                                        )}
                                    </FormattedMessage>
                                </Paragraph>
                            </Form>
                        )}
                    </Formik>
                )}
            </Fragment>
        )
    }
}

const StyledH6 = styled(H6)`
    text-align: center;
`

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px 0;
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    border: none;
`

const Paragraph = styled.div`
    ${props =>
        props.center
            ? `justify-content: center;
                                display: flex;`
            : ''};
`

const ReturnLink = styled.div`
    text-decoration: underline;
    cursor: pointer;
`

const RetryLink = styled(Text)`
    cursor: pointer;
    text-decoration: underline;
`

const StyledConnect = styled.div`
    ${style.buttonMedium};
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    background: ${colors.yellow};
    border: none;
    margin-top: 30px;
`

export { MailFormComponent }
